"use client"

const ViewStudent = ({ student, setViewingStudent, isProfile = false }) => {
  const title = isProfile ? "My Profile" : "Student Details / Admission Form"
  const showClose = !isProfile

  const handlePrint = () => {
    window.print()
  }

  // Helper component for a single info row (Label: Value)
  const InfoRow = ({ label, value, span = 1 }) => (
    <div className={`flex items-center col-span-${span} py-1`}>
      <span className="w-1/3 text-sm font-semibold text-gray-700">{label}:</span>
      <span className="w-2/3 text-sm font-medium border-b border-gray-300 pl-2">{value || "N/A"}</span>
    </div>
  )

  return (
    <>
      <style jsx>{`
        @media print {
          .no-print {
            display: none !important;
          }
          .print-page {
            margin: 0;
            padding: 15mm;
            font-size: 11pt;
            line-height: 1.6;
            color: #000;
            font-family: 'Times New Roman', Times, serif !important;
          }
        }
      `}</style>

      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white max-w-4xl w-full max-h-[90vh] overflow-y-auto rounded-lg shadow-2xl">
          {/* Header with Print and Close Buttons */}
          <div className="no-print sticky top-0 bg-white border-b border-gray-300 px-6 py-4 flex justify-between items-center z-10">
            <h1 className="text-2xl font-bold text-blue-700">{title}</h1>
            <div className="flex gap-2">
              <button
                onClick={handlePrint}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
                  />
                </svg>
                Print Document
              </button>
              {showClose && (
                <button
                  onClick={() => setViewingStudent(null)}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* Main Content */}
          <div className="p-6 print-page">
            {/* Institution Header */}
            <div className="text-center mb-6 pb-4 border-b-2 border-gray-700">
              <h2 className="text-2xl font-bold">[ITI / POLYTECHNIC NAME HERE]</h2>
              <p className="text-sm text-gray-600">[Institution Address, City, State, PIN]</p>
              <p className="text-xs text-gray-500">Contact: [Phone Number] | Email: [Email Address]</p>
              <h3 className="text-xl font-bold mt-4 text-blue-700 uppercase underline">Student Admission Form</h3>
              <div className="text-right text-sm text-gray-600 mt-2">Date: {new Date().toLocaleDateString()}</div>
            </div>

            {/* Basic Information Section */}
            <div className="mb-6 border border-gray-300 bg-gray-50">
              <h2 className="text-base font-bold text-blue-700 py-2 px-4 border-b border-gray-400 bg-gray-100 text-center uppercase">
                Basic Information
              </h2>
              <div className="p-4 relative">
                <div className="grid grid-cols-2 gap-x-8 gap-y-1 pr-32">
                  <InfoRow label="Student Name" value={student.name} />
                  <InfoRow label="Student Id" value={student.student_code} />
                  <InfoRow label="Email" value={student.email} />
                  <InfoRow label="Mobile No" value={student.mobile_no} />
                  <InfoRow label="Branch" value={student.branch_name} />
                  <InfoRow label="Trade" value={student.trade} />
                  <InfoRow label="Year" value={student.semester_or_year} />
                  <InfoRow
                    label="Admission Status"
                    value={student.admission_status === null ? "Pending" : student.admission_status}
                  />
                  <InfoRow label="Institute" value={student.department_name || student.department?.name || "N/A"} />
                  <InfoRow
                    label="Enrollment No."
                    value={student.enrollment_number === null ? "Not enrolled" : student.enrollment_number}
                  />

                  {/* <InfoRow
                    label="Assigned Faculty"
                    value={student.faculty_name || student.assigned_faculty_name || student.faculty?.name}
                  /> */}
                  <InfoRow label="Council" value={student.council} />
                  <InfoRow label="Scholarship" value={student.scholarship} />
                </div>

                {/* Student Photo */}
                <div className="absolute top-4 right-4 w-28 h-32 border-2 border-gray-400 bg-gray-200 flex items-center justify-center overflow-hidden">
                  {student.profile_photo_path ? (
                    <img
                      src={`http://localhost:3000/${student.profile_photo_path}`}
                      alt="Student"
                      className="object-cover w-full h-full"
                      onError={(e) => {
                        e.target.onerror = null
                        e.target.src = "https://placehold.co/120x150/f0f0f0/888?text=Photo+N%2FA"
                      }}
                    />
                  ) : (
                    <span className="text-xs text-gray-500">Photo N/A</span>
                  )}
                </div>
              </div>
            </div>

            {/* Personal Information Section */}
            <div className="mb-6 border border-gray-300 bg-gray-50">
              <h2 className="text-base font-bold text-blue-700 py-2 px-4 border-b border-gray-400 bg-gray-100 text-center uppercase">
                Personal Information
              </h2>
              <div className="p-4 grid grid-cols-3 gap-x-8 gap-y-1">
                <InfoRow
                  label="Date of Birth"
                  value={student.dob ? new Date(student.dob).toLocaleDateString() : "N/A"}
                />
                <InfoRow label="Gender" value={student.gender} />
                <InfoRow label="Blood Group" value={student.blood_group} />
                <InfoRow label="Father's Name" value={student.fathers_name} />
                <InfoRow label="Mother's Name" value={student.mothers_name} />
                <InfoRow label="Aadhar No" value={student.aadhar_card_no} />
              </div>
            </div>

            {/* Address Information Section */}
            <div className="mb-6 border border-gray-300 bg-gray-50">
              <h2 className="text-base font-bold text-blue-700 py-2 px-4 border-b border-gray-400 bg-gray-100 text-center uppercase">
                Address Information
              </h2>
              <div className="p-4">
                <p className="font-bold text-sm text-blue-700 mb-2">Permanent Address</p>
                <div className="grid grid-cols-4 gap-x-8 gap-y-1 mb-4">
                  <InfoRow label="Block" value={student.block} />
                  <InfoRow label="District" value={student.district} />
                  <InfoRow label="PIN Code" value={student.pin_code} />
                  <InfoRow label="State" value={student.state} />
                </div>

                <p className="font-bold text-sm text-blue-700 mb-2 mt-4">Correspondence Address</p>
                <div className="grid grid-cols-4 gap-x-8 gap-y-1">
                  <InfoRow label="Block" value={student.correspondence_block} />
                  <InfoRow label="District" value={student.correspondence_district} />
                  <InfoRow label="PIN Code" value={student.correspondence_pincode} />
                  <InfoRow label="State" value={student.correspondence_state} />
                </div>
              </div>
            </div>

            {/* Category & Documents Section */}
            <div className="mb-6 border border-gray-300 bg-gray-50">
              <h2 className="text-base font-bold text-blue-700 py-2 px-4 border-b border-gray-400 bg-gray-100 text-center uppercase">
                Category & Documents
              </h2>
              <div className="p-4 grid grid-cols-3 gap-x-8 gap-y-1">
                {/* <InfoRow label="Caste" value={student.caste} /> */}
                <InfoRow label="Caste" value={student.category} />
                <InfoRow label="Sub Category" value={student.sub_category} />
                <InfoRow label="Caste Certificate No" value={student.caste_cert_no} />
                <InfoRow label="TC Serial No" value={student.tc_serial_no} />
                <InfoRow label="Medical Certificate No" value={student.medical_fitness_cert_no} />
                <InfoRow label="Driving License No" value={student.driving_license_no} />
                <InfoRow label="Anti Ragging Reg No" value={student.anti_ragging_reg_no} />
                <InfoRow label="PAN Card No" value={student.pan_card_no} />
              </div>
            </div>

            {/* Academic Information Section */}
            <div className="mb-6 border border-gray-300 bg-gray-50">
              <h2 className="text-base font-bold text-blue-700 py-2 px-4 border-b border-gray-400 bg-gray-100 text-center uppercase">
                Academic Information
              </h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-100">
                    <tr>
                      {["Level", "Board/University", "Roll No", "Passing Year", "Percent Marks", "Max Marks"].map(
                        (header) => (
                          <th
                            key={header}
                            className="px-4 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-300 last:border-r-0"
                          >
                            {header}
                          </th>
                        ),
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {[
                      { level: "High School", prefix: "highschool" },
                      { level: "Graduation", prefix: "graduation" },
                      { level: "International", prefix: "international" },
                    ].map(({ level, prefix }) => (
                      <tr key={level}>
                        <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-300">
                          {level}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500 border-r border-gray-300">
                          {student[`${prefix}_board_or_university`] || "N/A"}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500 border-r border-gray-300">
                          {student[`${prefix}_roll_no`] || "N/A"}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500 border-r border-gray-300">
                          {student[`${prefix}_passing_year`] || "N/A"}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500 border-r border-gray-300">
                          {student[`${prefix}_percent_marks`] || "N/A"}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                          {student[`${prefix}_max_marks`] || "N/A"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Payment Information Section */}
            <div className="mb-6 border border-gray-300 bg-gray-50">
              <h2 className="text-base font-bold text-blue-700 py-2 px-4 border-b border-gray-400 bg-gray-100 text-center uppercase">
                Payment Information
              </h2>
              <div className="p-4 grid grid-cols-2 gap-8">
                <div>
                  <p className="font-bold text-sm text-blue-700 mb-2 border-b border-blue-300 pb-1">JEECUP Details</p>
                  <div className="space-y-1">
                    <InfoRow label="Roll No" value={student.jeecup_roll_no} span={1} />
                    <InfoRow label="Amount" value={student.jeecup_amount} span={1} />
                    <InfoRow
                      label="Date"
                      value={student.jeecup_date ? new Date(student.jeecup_date).toLocaleDateString() : "N/A"}
                      span={1}
                    />
                    <InfoRow label="Transaction No" value={student.jeecup_transaction_no} span={1} />
                  </div>
                </div>
                <div>
                  <p className="font-bold text-sm text-blue-700 mb-2 border-b border-blue-300 pb-1">
                    Institute Fee Details
                  </p>
                  <div className="space-y-1">
                    <InfoRow label="Amount" value={student.institute_amount} span={1} />
                    <InfoRow
                      label="Date"
                      value={student.institute_date ? new Date(student.institute_date).toLocaleDateString() : "N/A"}
                      span={1}
                    />
                    <InfoRow label="Transaction No" value={student.institute_transaction_no} span={1} />
                    <InfoRow label="Hostel Applied" value={student.applied_for_hostel} span={1} />
                  </div>
                </div>
              </div>
            </div>

            {/* Declaration Section */}
            <div className="mb-6 border border-gray-300 bg-gray-50">
              <h2 className="text-base font-bold text-blue-700 py-2 px-4 border-b border-gray-400 bg-gray-100 text-center uppercase">
                Declaration
              </h2>
              <div className="p-4">
                <p className="text-sm mb-4 text-gray-700 leading-relaxed">
                  I hereby declare that the information provided above is true to the best of my knowledge and belief. I
                  understand that any false information may lead to the cancellation of my admission.
                </p>
                <div className="grid grid-cols-2 gap-8 mt-6">
                  <div className="flex items-center">
                    <span className="w-1/3 text-sm font-semibold text-gray-700">Student's Signature:</span>
                    <span className="w-2/3 border-b border-gray-300"></span>
                  </div>
                  <div className="flex items-center">
                    <span className="w-1/3 text-sm font-semibold text-gray-700">Date:</span>
                    <span className="w-2/3 border-b border-gray-300 pl-2 text-sm">
                      {new Date().toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="text-center text-xs text-gray-500 border-t border-gray-300 pt-4 mt-6">
              This is a computer-generated document. For any queries, please contact the admission office.
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default ViewStudent
